import React, { useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Users,
  Plus,
  CheckCircle,
  XCircle,
  AlertTriangle,
  ArrowRight,
  MoreHorizontal,
  RefreshCw,
  Ban,
  Zap,
  Pencil,
} from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Spinner } from '../ui/spinner';
import {
  billingRelationshipLabel,
  filterConsultantClients,
  getClientUnusableReason,
  isClientUsable,
  planTypeLabel,
  resolveClientPriceLabel,
  resolveCommitmentEndLabel,
  resolveClientStatus,
  statusBadgeConfig,
} from '../utils/consultantClientUtils';

/**
 * Consultant client portfolio — billing-first dashboard.
 */
const PortfolioDashboard = ({
  clients = [],
  renewals = [],
  schedules = [],
  masterPerReportFeeVal = null,
  pendingContracts = {},
  consultantParentId,
  loading = false,
  actionLoading = {},
  onAddClient,
  onOnboardExisting,
  onActivate,
  onResend,
  onVoid,
  onAmend,
  onRefresh,
}) => {
  const history = useHistory();
  const [tab, setTab] = useState('all');

  const clientRows = useMemo(
    () => filterConsultantClients(clients, consultantParentId),
    [clients, consultantParentId],
  );

  const renewalsByOrgId = useMemo(() => {
    const map = {};
    (renewals || []).forEach((r) => {
      map[r.organisationId] = r;
    });
    return map;
  }, [renewals]);

  const schedulesByOrgId = useMemo(() => {
    const map = {};
    (schedules || []).forEach((s) => {
      if (s.organisationId) map[s.organisationId] = s;
    });
    return map;
  }, [schedules]);

  const summary = useMemo(() => {
    const total = clientRows.length;
    const active = clientRows.filter((c) => resolveClientStatus(c) === 'Active').length;
    const pending = clientRows.filter((c) =>
      ['PendingActivation', 'PendingAgreement'].includes(resolveClientStatus(c)),
    ).length;
    const renewalDue = (renewals || []).length;
    const reportReady = clientRows.filter((c) => isClientUsable(c, renewalsByOrgId[c.id])).length;
    return { total, active, pending, renewalDue, reportReady };
  }, [clientRows, renewals, renewalsByOrgId]);

  const displayRows = tab === 'renewals' ? clientRows.filter((c) => renewalsByOrgId[c.id]) : clientRows;

  const renderReportAccess = (client) => {
    const renewal = renewalsByOrgId[client.id];
    const usable = isClientUsable(client, renewal);
    if (usable) {
      return (
        <span className='inline-flex items-center gap-1 text-success text-xs'>
          <CheckCircle size={14} strokeWidth={1.5} />
          Ready
        </span>
      );
    }
    const reason = getClientUnusableReason(client);
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className='inline-flex items-center gap-1 text-muted-foreground text-xs cursor-help'>
            <XCircle size={14} strokeWidth={1.5} />
            Blocked
          </span>
        </TooltipTrigger>
        <TooltipContent className='bg-popover text-popover-foreground border-border max-w-xs'>
          {reason}
        </TooltipContent>
      </Tooltip>
    );
  };

  const renderActions = (client) => {
    const status = resolveClientStatus(client);
    const relationship = client.billingRelationship;
    const contractId = pendingContracts[client.id];
    const renewal = renewalsByOrgId[client.id];
    const isRowLoading = Object.keys(actionLoading).some((k) => actionLoading[k] && k.includes(client.id));

    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant='ghost' size='sm' disabled={isRowLoading} onClick={(e) => e.stopPropagation()}>
            {isRowLoading ? <Spinner className='h-4 w-4' /> : <MoreHorizontal size={16} strokeWidth={1.5} />}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align='end' className='bg-popover text-popover-foreground border-border'>
          <DropdownMenuItem onClick={() => history.push(`/consultant/client/${client.id}`)}>
            View details
          </DropdownMenuItem>
          {status === 'PendingActivation' && relationship === 'consultant_billed' && (
            <DropdownMenuItem onClick={() => onActivate?.(client.id)}>
              <Zap size={14} className='mr-2' strokeWidth={1.5} />
              Activate
            </DropdownMenuItem>
          )}
          {['PendingActivation', 'PendingAgreement'].includes(status) && relationship === 'direct_billed' && (
            <>
              <DropdownMenuItem
                disabled={!contractId}
                onClick={() => contractId && onResend?.(client.id, contractId)}>
                <RefreshCw size={14} className='mr-2' strokeWidth={1.5} />
                Resend invitation
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={!contractId}
                onClick={() => contractId && onVoid?.(client.id, contractId)}
                className='text-destructive'>
                <Ban size={14} className='mr-2' strokeWidth={1.5} />
                Void invitation
              </DropdownMenuItem>
            </>
          )}
          {status === 'Active' && relationship === 'consultant_billed' && (
            <DropdownMenuItem onClick={() => onAmend?.(client)}>
              <Pencil size={14} className='mr-2' strokeWidth={1.5} />
              Change plan
            </DropdownMenuItem>
          )}
          {renewal && (
            <DropdownMenuItem onClick={() => onAmend?.(client, renewal)}>
              <AlertTriangle size={14} className='mr-2' strokeWidth={1.5} />
              Renew / amend
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  return (
    <TooltipProvider>
      <div className='flex flex-col gap-4'>
        <div className='flex items-center justify-between flex-wrap gap-3'>
          <h2 className='text-lg font-semibold flex items-center gap-2 text-foreground'>
            <Users size={20} strokeWidth={1.5} />
            Client Portfolio
          </h2>
          <div className='flex gap-2'>
            <Button variant='outline' size='sm' onClick={onRefresh} disabled={loading}>
              Refresh
            </Button>
            {onOnboardExisting && (
              <Button variant='outline' size='sm' onClick={onOnboardExisting}>
                Onboard existing
              </Button>
            )}
            <Button size='sm' onClick={onAddClient}>
              <Plus size={14} strokeWidth={1.5} />
              Add client
            </Button>
          </div>
        </div>

        <div className='grid grid-cols-2 md:grid-cols-5 gap-3'>
          <Card className='bg-card'>
            <CardContent className='p-4 text-center'>
              <p className='text-2xl font-bold text-foreground'>{summary.total}</p>
              <p className='text-xs text-muted-foreground'>Total clients</p>
            </CardContent>
          </Card>
          <Card className='bg-card'>
            <CardContent className='p-4 text-center'>
              <p className='text-2xl font-bold text-success'>{summary.active}</p>
              <p className='text-xs text-success'>Active</p>
            </CardContent>
          </Card>
          <Card className='bg-card'>
            <CardContent className='p-4 text-center'>
              <p className='text-2xl font-bold text-warning'>{summary.pending}</p>
              <p className='text-xs text-warning'>Pending setup</p>
            </CardContent>
          </Card>
          <Card className='bg-card'>
            <CardContent className='p-4 text-center'>
              <p className='text-2xl font-bold text-info'>{summary.renewalDue}</p>
              <p className='text-xs text-info'>Renewals due</p>
            </CardContent>
          </Card>
          <Card className='bg-card'>
            <CardContent className='p-4 text-center'>
              <p className='text-2xl font-bold text-foreground'>{summary.reportReady}</p>
              <p className='text-xs text-muted-foreground'>Report-ready</p>
            </CardContent>
          </Card>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className='bg-muted'>
            <TabsTrigger value='all'>All clients</TabsTrigger>
            <TabsTrigger value='renewals'>Renewals ({summary.renewalDue})</TabsTrigger>
          </TabsList>

          <TabsContent value={tab} className='mt-4'>
            <Card className='bg-card'>
              <CardContent className='p-0'>
                {loading ? (
                  <div className='flex justify-center py-12'>
                    <Spinner className='h-8 w-8' />
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow className='border-border'>
                        <TableHead>Client</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Billing</TableHead>
                        <TableHead>Plan</TableHead>
                        <TableHead>Price</TableHead>
                        <TableHead>Renewal</TableHead>
                        <TableHead>Reports</TableHead>
                        <TableHead />
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {displayRows.map((client) => {
                        const statusCfg = statusBadgeConfig(client);
                        const renewal = renewalsByOrgId[client.id];
                        const schedule = schedulesByOrgId[client.id];
                        const currency = client.currency || '£';
                        return (
                          <TableRow
                            key={client.id}
                            className='border-border cursor-pointer hover:bg-muted/50'
                            onClick={() => history.push(`/consultant/client/${client.id}`)}>
                            <TableCell className='font-medium text-sm text-foreground'>
                              {client.name || client.accountName}
                            </TableCell>
                            <TableCell>
                              <Badge className={statusCfg.className}>{statusCfg.label}</Badge>
                            </TableCell>
                            <TableCell className='text-sm text-muted-foreground'>
                              {billingRelationshipLabel(client.billingRelationship)}
                            </TableCell>
                            <TableCell className='text-sm text-muted-foreground'>
                              {planTypeLabel(schedule?.planType || renewal?.planType, client)}
                            </TableCell>
                            <TableCell className='text-sm text-muted-foreground'>
                              {resolveClientPriceLabel(client, schedule || renewal, masterPerReportFeeVal, currency)}
                            </TableCell>
                            <TableCell className='text-xs text-muted-foreground'>
                              {renewal?.commitmentEndDate
                                ? `${renewal.commitmentEndDate}${renewal.daysUntilCommitmentEnd != null ? ` (${renewal.daysUntilCommitmentEnd}d)` : ''}`
                                : resolveCommitmentEndLabel(client, schedule)}
                            </TableCell>
                            <TableCell>{renderReportAccess(client)}</TableCell>
                            <TableCell onClick={(e) => e.stopPropagation()}>{renderActions(client)}</TableCell>
                          </TableRow>
                        );
                      })}
                      {displayRows.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={8} className='text-center py-8 text-muted-foreground'>
                            {tab === 'renewals' ? 'No renewals due in the next 60 days.' : 'No clients found.'}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </TooltipProvider>
  );
};

export default PortfolioDashboard;
